<template>
  <div class="w-full flex flex-col items-center justify-center py-12 text-center min-h-[260px]">
    <template v-if="isLoading">
      <LayupSpinner />
      <p class="mt-4 text-gray-600 text-center">Verifying payment status...</p>
    </template>

    <template v-else-if="isSuccess">
      <div class="flex items-center justify-center w-14 h-14 rounded-full border-2 border-teal-400 mb-6">
        <Check class="w-7 h-7 text-teal-400" stroke-width="2.5" />
      </div>
      <p class="text-2xl font-bold text-gray-800 mb-2">Your payment was successful</p>
      <p class="text-gray-500 text-sm">Thank you for making your due payment.</p>
    </template>
    <template v-else>
      <div class="flex items-center justify-center w-14 h-14 rounded-full border-2 border-red-400 mb-6">
        <AlertCircle class="w-7 h-7 text-red-400" stroke-width="2.5" />
      </div>
      <p class="text-xl font-bold text-gray-800 mb-4 max-w-[280px]">
        There was an issue. Please contact support, or try again later.
      </p>
      <div class="flex gap-3 mt-2">
        <button
          class="px-5 py-2.5 rounded-lg border-2 border-teal-600 text-teal-600 font-semibold text-sm hover:bg-teal-50"
          @click="onContactSupport"
        >
          Contact Support
        </button>
        <button
          class="px-5 py-2.5 rounded-lg bg-teal-600 text-white font-semibold text-sm hover:bg-teal-700"
          @click="emit('retry')"
        >
          Retry
        </button>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import LayupSpinner from '@/components/common/LayupSpinner.vue';
import { NetworkError } from '@/errors';
import { getPayment } from '@/services/payments';
import { addMetric, createException, createWarning } from '@/utils/monitoring.utils';
import { delay } from '@/utils/time.utils';
import { AlertCircle, Check } from 'lucide-vue-next';
import { onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useToast } from 'vue-toast-notification';

const props = defineProps<{
  paymentId: string;
}>();

const emit = defineEmits<{
  done: [];
  retry: [];
}>();

const router = useRouter();
const isLoading = ref(true);
const isSuccess = ref(false);
const toast = useToast();

const waitForPaymentStatus = async (id: string) => {
  let payment = await getPayment(id);

  while (payment?.pending) {
    await delay(3000);
    payment = await getPayment(id);
  }
  return payment;
};

const verifyPayment = async () => {
  isLoading.value = true;
  let paymentProvider: string;
  let paymentMethod: string;

  try {
    const payment = await waitForPaymentStatus(props.paymentId);

    paymentProvider = payment.connectorData?.paymentProvider?.toLowerCase() ?? 'unknown';
    paymentMethod = payment.connectorData?.paymentMethod?.toLowerCase() ?? 'unknown';

    isSuccess.value = !!payment?.paid;

    addMetric('accept_payment', {
      status: payment?.paid ? 'success' : 'failed',
      payment_provider: paymentProvider,
      payment_method: paymentMethod,
      channel: 'shopper_contribute',
    });
  } catch (error: any) {
    isSuccess.value = false;
    let errorMessage = 'An unexpected error occurred while verifying payment.';

    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric('accept_payment', { status: 'network_error', channel: 'shopper_contribute' });
      errorMessage = 'Network error. Please check your connection and try again.';
    } else {
      createException(error);
      addMetric('accept_payment', { status: 'error', channel: 'shopper_contribute' });

      if (error?.response?.data) {
        errorMessage = error.response.data;
      }
    }

    toast.open({ message: errorMessage, type: 'error', position: 'top' });
  } finally {
    isLoading.value = false;
  }
};

watch(isSuccess, (val) => {
  if (val) {
    setTimeout(() => emit('done'), 3000);
  }
});

const onContactSupport = () => {
  emit('done');
  router.push({ name: 'shopperContactUs' });
};

onMounted(() => {
  verifyPayment();
});
</script>
